import { Inject, Injectable, PipeTransform } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { EntityService } from '@services/entity/entity.service';
import { Observable } from 'rxjs';
import { InvoiceDto } from '@dto/invoiceDto';
import { InvoiceItemDto } from '@dto/invoiceItemDto';
import { ChargingParkSubscriptionDto } from '@dto/chargingParkSubscriptionDto';
import { InvoiceSettingsDto } from '@dto/invoiceSettingsDto';


@Injectable({
  providedIn: 'root'
})
export class InvoicesService extends EntityService {

  public entityRoute = "invoices";

  constructor(http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    super(http, baseUrl);
  }

  createInvoice(invoice: InvoiceDto): Observable<InvoiceDto> {
    return this.http.post<InvoiceDto>(this.baseUrl + 'api/invoices', invoice);
  }
  
  getInvoicesOfCustomer(userId: string): Observable<InvoiceDto[]> {
    return this.http.get<InvoiceDto[]>(this.baseUrl + 'api/invoices/customer/' + userId);
  }
  
  getInvoicesOfRecipient(userId: string): Observable<InvoiceDto[]> {
    return this.http.get<InvoiceDto[]>(this.baseUrl + 'api/invoices/recipient/' + userId);
  }

  getInvoice(id: number): Observable<InvoiceDto> {
    return this.http.get<InvoiceDto>(this.baseUrl + 'api/invoices/' + id);
  }

  getInvoiceOfCurrentUser(invoiceId: number): Observable<InvoiceDto> {
    return this.http.get<InvoiceDto>(this.baseUrl + 'api/invoices/' + invoiceId + '/currentuser');
  }

  changeInvoiceOfUser(change: ChargingParkSubscriptionDto): Observable<Boolean> {
    return this.http.patch<Boolean>(this.baseUrl + 'api/invoices/' + change.userId + '/change', change);
  }

  deleteInvoice(invoiceId: string): Observable<Boolean> {
    return this.http.delete<Boolean>(this.baseUrl + 'api/invoices/' + invoiceId);
  }

  getAllUnbilledInvoiceItemsOfIssuerForManualBillingAndAutoBilling(issuerUserId: string): Observable<InvoiceItemDto[]> {
    return this.http.get<InvoiceItemDto[]>(this.baseUrl + 'api/invoices/items/issuer/' + issuerUserId);
  }

  getAllUnbilledInvoiceItemsOfRecipientForManualBillingAndAutoBilling(recipientUserId: string): Observable<InvoiceItemDto[]> {
    return this.http.get<InvoiceItemDto[]>(this.baseUrl + 'api/invoices/items/recipient/' + recipientUserId);
  }

  createInvoiceSettings(invoiceSettings: InvoiceSettingsDto): Observable<InvoiceSettingsDto> {
    return this.http.post<InvoiceSettingsDto>(this.baseUrl + 'api/invoices/settings', invoiceSettings);
  }

  patchInvoiceSettings(invoiceSettings: InvoiceSettingsDto): Observable<Boolean> {
    return this.http.patch<Boolean>(this.baseUrl + 'api/invoices/settings/' + invoiceSettings.id, invoiceSettings);
  }

  getInvoiceSettingsOfUser(userId: string): Observable<InvoiceSettingsDto> {
    return this.http.get<InvoiceSettingsDto>(this.baseUrl + 'api/invoices/settings/user/' + userId);
  }

  validateIBAN(iban: string): Observable<any> {
    return this.http.get<any>(`https://openiban.com/validate/${iban}?getBIC=true&validateBankCode=true`);
  }

  getInvoicePdf(invoiceId: number): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/invoices/download/' + invoiceId, { responseType: 'blob' as 'json'});
  }

  downloadPreview(ownerUserId: string, invoiceSettings: InvoiceSettingsDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/invoices/download/preview/${ownerUserId}`, invoiceSettings, { responseType: 'blob' as 'json'});
  }

  static matches(invoice: InvoiceDto, term: string, pipe?: PipeTransform) {
    return invoice.invoiceNumber?.toLowerCase().includes(term.toLowerCase())
      || invoice.ownerUserId?.toLowerCase().includes(term.toLowerCase())
  }
}
